import { type Common } from '../common.js';
import {
  type CareGapDefinition,
  type Icd10,
  type ModelName,
  type ProcessDocument,
  type ProcessLabValue,
  type ProcessMedication,
  type ProcessProcedure,
  type RecommendationKind,
} from '../models/patient/clinicalAlgorithmsData.js';
import type { NonPersistedFinding } from '../models/patient/finding.js';
import type { FileDocument, OrganizationDocument } from '../models/predict/document.js';

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async embedAndStoreFiles(
    args:
      | { files: FileDocument[]; organizationId: string }
      | { files: FileDocument[]; documentClassName: string }
  ) {
    const url = this.common.constructPath(`/predict/documents/text/embed-and-store-files`);
    return await this.common.postJSON(url, args);
  }

  async createFilesForClassname(
    classname: string,
    files: FileDocument[]
  ): Promise<{ uuids: string[] }> {
    const url = this.common.constructPath(
      `/predict/documents/text/classnames/${classname}/documents`
    );
    return await this.common.postJSON(url, { files });
  }

  async findAllFilesByClassname(args: {
    classname: string;
    chunked?: boolean;
  }): Promise<OrganizationDocument[]> {
    const { classname, chunked } = args;
    const url = this.common.constructPath(
      `/predict/documents/text/classnames/${classname}/documents`,
      { chunked }
    );
    const { documents } = await this.common.getJSON(url);
    return documents;
  }

  async findFileByClassnameAndId(classname: string, id: string): Promise<OrganizationDocument> {
    const url = this.common.constructPath(
      `/predict/documents/text/classnames/${classname}/documents/${id}`
    );
    return await this.common.getJSON(url);
  }

  async updateFileForClassnameById(args: {
    classname: string;
    id: string;
    values: Pick<OrganizationDocument, 'url' | 'allowExternal'>;
  }): Promise<OrganizationDocument> {
    const { classname, id, values } = args;
    const url = this.common.constructPath(
      `/predict/documents/text/classnames/${classname}/documents/${id}`
    );
    return await this.common.patchJSON(url, values);
  }

  async deleteFileForClassnameById(args: {
    classname: string;
    id: string;
    collated?: boolean;
  }): Promise<{ success: true }> {
    const { classname, id, collated } = args;
    const url = this.common.constructPath(
      `/predict/documents/text/classnames/${classname}/documents/${id}`,
      { collated }
    );
    return await this.common.deleteJSON(url);
  }

  async extractCareGapFindings(args: {
    ehrInformationId: string;
    documents: ProcessDocument[];
    icd10s: Icd10[];
    medications: ProcessMedication[];
    labValues: ProcessLabValue[];
    procedures: ProcessProcedure[];
    dateOfBirth?: string;
    deceased?: boolean;
    sex?: string;
    careGapTypes?: RecommendationKind[];
    completionModelName?: ModelName;
    customCareGapDefinition?: CareGapDefinition;
  }): Promise<{ findings?: NonPersistedFinding[] }> {
    const url = this.common.constructPath('/predict/documents/text/extract-care-gap-findings');
    return await this.common.postJSON(url, { ...args });
  }

  async getCareGapAlgorithms(): Promise<string[]> {
    const url = this.common.constructPath('/predict/documents/text/clinical-algorithm');
    return await this.common.getJSON(url);
  }

  async getCareGapAlgorithmDefinition(careGapType: string): Promise<string> {
    const url = this.common.constructPath('/predict/documents/text/clinical-algorithm', {
      careGapType,
    });
    return await this.common.getJSON(url);
  }
}
